// useFeedFreshness — derives a "is the hydra feed actually flowing" flag
// from marketStore. Ticks every 1s so the indicator turns yellow even
// when no new messages arrive to trigger a marketStore version bump.
//
// Two signals combined — stale if EITHER fails:
//   1. Global silence: no message in any source/ticker for >1s.
//      The tight threshold matches a healthy hydra cadence (multiple
//      frames per second across ~50 markets).
//   2. Mostly-dead activity: < 10% of tracked tickers received any
//      frame in the last 30s. Catches the failure mode where one
//      chatty ticker keeps lastMessageMs warm while the rest of the
//      cache is silent (the original "lying green dot" bug).
//
// The 1s tick re-evaluates both signals from a fresh Date.now() vs
// the stored values, so the dot turns yellow within a second of either
// condition becoming true even if no data flows in.

import { useEffect, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';

export interface FeedFreshness {
  /** True when the WS thinks it's connected but no message has arrived
   *  in STALE_THRESHOLD_MS, OR the activity fraction has collapsed
   *  below ACTIVITY_THRESHOLD over ACTIVITY_WINDOW_MS, OR the WS has
   *  never received a frame. */
  isStale: boolean;
  /** Seconds since the most recent message. Null until the first
   *  message arrives. */
  secondsSinceUpdate: number | null;
  /** Mirror of marketStore.connected — useful when consumers want to
   *  distinguish "WS dropped" from "WS open but silent". */
  connected: boolean;
  /** True while hydra has this connection throttled at backpressure
   *  level >= 2 — orderbook frames are suspended server-side (market +
   *  consensus keep flowing, so isStale stays false and the dot stays
   *  green). Orderbook-driven views must surface this: the books on
   *  screen are frozen. Clears when orderbook frames resume. */
  orderbooksPaused: boolean;
}

const STALE_THRESHOLD_MS = 1_000;
const ACTIVITY_WINDOW_MS = 30_000;
const ACTIVITY_THRESHOLD = 0.10;
const ACTIVITY_MIN_POP = 20;
const TICK_MS = 1_000;

export function useFeedFreshness(): FeedFreshness {
  const connected = useMarketStore((s) => s.connected);
  const lastMessageMs = useMarketStore((s) => s.lastMessageMs);
  const orderbooksPaused = useMarketStore((s) => s.backpressureLevel >= 2);
  const [tickNow, setTickNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setTickNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const hasEverReceived = lastMessageMs > 0;
  const ageMs = hasEverReceived ? tickNow - lastMessageMs : null;
  const recentSilence = !connected || ageMs == null || ageMs > STALE_THRESHOLD_MS;

  // Read activity counts off the store every tick. Cheap (one Map walk)
  // and necessary because the store doesn't auto-update this value when
  // nothing is being ingested — we have to recompute on the clock.
  const { active, total } = useMarketStore.getState().getActivityCount(ACTIVITY_WINDOW_MS);
  const enoughPopulation = total >= ACTIVITY_MIN_POP;
  const activityCollapse = enoughPopulation && active / total < ACTIVITY_THRESHOLD;

  const isStale = recentSilence || activityCollapse;
  const secondsSinceUpdate = ageMs == null ? null : Math.max(0, Math.floor(ageMs / 1000));

  return { isStale, secondsSinceUpdate, connected, orderbooksPaused };
}
